import { useEffect, useId, useRef, useState } from 'react';
import type { AccountUsage } from '@aimo/contracts/web';
import type { ApiClient } from '../api.js';
import { Popover, PopoverAnchor, PopoverContent } from './popover.js';
import { usageText as text } from './account-usage.text.js';

const number = (value: number) => value.toLocaleString();
const time = (value: string) => new Date(value).toLocaleString();

export function AccountUsageDetails({ usage }: { usage: AccountUsage }) {
  const rows = [
    [text.tokens, number(usage.totalTokens)], [text.calls, number(usage.calls)],
    [text.input, number(usage.inputTokens)], [text.output, number(usage.outputTokens)],
    [text.cacheRead, number(usage.cacheReadTokens)], [text.cacheWrite, number(usage.cacheWriteTokens)],
    [text.cost, `$${usage.estimatedCostUsd.toFixed(4)}`],
  ];
  return <>
    {usage.calls === 0 && <p>{text.empty}</p>}
    <dl className="my-3 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
      {rows.map(([label, value]) => <div key={label} className="contents"><dt className="text-muted-foreground">{label}</dt><dd className="m-0 text-right font-medium tabular-nums">{value}</dd></div>)}
    </dl>
    <p className="my-1 text-xs text-muted-foreground">{text.basis}</p>
    {usage.missingCostCalls > 0 && <p className="my-1 text-xs">{usage.missingCostCalls} {text.missing}</p>}
    <p className="my-1 text-xs text-muted-foreground">{text.coverage}</p>
    {usage.periodStart && usage.periodEnd && <p className="my-1 text-xs text-muted-foreground">{text.period}: {time(usage.periodStart)} – {time(usage.periodEnd)}</p>}
    <p className="my-1 text-xs text-muted-foreground">{text.asOf}: {time(usage.asOf)}</p>
  </>;
}

/** Keyed by principal so no request or snapshot can survive an account change. */
export function AccountAvatar({ accountName, api }: { accountName: string; api: ApiClient | null }) {
  return <AccountAvatarSession key={api?.session?.principal.principalId ?? accountName} accountName={accountName} api={api} />;
}

function AccountAvatarSession({ accountName, api }: { accountName: string; api: ApiClient | null }) {
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState<AccountUsage | null>(null);
  const [failed, setFailed] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const id = useId();
  const keepOpen = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const closeSoon = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 180); };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!open) return;
    const abort = new AbortController();
    setUsage(null); setFailed(false);
    if (!api) { setFailed(true); return; }
    void api.request<AccountUsage>('getAccountUsage', { signal: abort.signal })
      .then(value => { if (!abort.signal.aborted) setUsage(value); })
      .catch(() => { if (!abort.signal.aborted) setFailed(true); });
    return () => abort.abort();
  }, [open, api]);
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverAnchor asChild>
      <button type="button" aria-label={text.label(accountName)} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
        onPointerEnter={event => { if (event.pointerType !== 'touch') keepOpen(); }} onPointerLeave={closeSoon}
        onFocus={keepOpen} onBlur={closeSoon} onClick={keepOpen}
        className="flex size-8 cursor-pointer items-center justify-center rounded-full border-0 bg-(--md-sys-color-surface-container-highest) text-[13px] font-medium text-(--md-sys-color-on-surface-variant) outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {(accountName.trim()[0] ?? 'A').toUpperCase()}
      </button>
    </PopoverAnchor>
    <PopoverContent id={id} align="end" aria-label={text.title} className="w-80 max-w-[calc(100vw-20px)] p-4"
      onPointerEnter={keepOpen} onPointerLeave={closeSoon} onFocusCapture={keepOpen} onBlurCapture={closeSoon}
      onOpenAutoFocus={event => event.preventDefault()} onCloseAutoFocus={event => event.preventDefault()}>
      <strong>{accountName}</strong>
      <div role="status" aria-live="polite" aria-busy={!failed && !usage}>
        {usage ? <AccountUsageDetails usage={usage} /> : <p className="text-muted-foreground">{failed ? text.unavailable : text.loading}</p>}
      </div>
    </PopoverContent>
  </Popover>;
}
